import React, { useState, useMemo } from 'react';
import { X, Copy, Check, User, Key, MessageCircle, Send } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import LoadingSpinner from '../common/LoadingSpinner';
import QRCode from 'react-qr-code';

// Link app (WEBAPP-015) – in futuro da env
const APP_LINK_ANDROID = 'https://play.google.com/store/apps/details?id=com.fitgymtrack.app';
const APP_LINK_IOS = 'https://apps.apple.com/app/fitgymtrack/id647906686';

export default function ResetPasswordModal({
    isOpen,
    onClose,
    member,
    onResetPassword
}) {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [resetResult, setResetResult] = useState(null);
    const [copied, setCopied] = useState(false);
    const [copiedMessage, setCopiedMessage] = useState(false);

    const credentialsMessage = useMemo(() => {
        if (!resetResult?.temp_password || !resetResult?.user?.username) return '';
        return t('gym.resetPasswordModal.credentialsMessage', {
            username: resetResult.user.username,
            password: resetResult.temp_password,
            linkAndroid: APP_LINK_ANDROID,
            linkIos: APP_LINK_IOS
        });
    }, [resetResult, t]);

    // Formato Italia: +39 340 001 1223 → 393400011223 per wa.me
    const normalizePhoneForWhatsApp = (phone) => {
        let digits = String(phone ?? '').replace(/\D/g, '');
        if (digits.length === 0) return '';
        if (digits.startsWith('00')) digits = digits.replace(/^00/, '');
        if (digits.startsWith('39') && digits.length >= 11) return digits;
        if (digits.length >= 9 && digits.length <= 10) return '39' + digits;
        return digits;
    };

    const handleReset = async () => {
        if (!member) return;

        setLoading(true);
        try {
            const result = await onResetPassword(member.id, member.name);
            if (result) {
                setResetResult(result);
            }
        } catch (error) {
            console.error('Errore nel reset password:', error);
            toast.error(t('gym.resetPasswordModal.errorReset'));
        } finally {
            setLoading(false);
        }
    };

    const handleCopy = async () => {
        if (resetResult?.temp_password) {
            try {
                await navigator.clipboard.writeText(resetResult.temp_password);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
                toast.success(t('gym.resetPasswordModal.copiedPassword'));
            } catch (err) {
                console.error('Errore nella copia:', err);
            }
        }
    };

    const handleCopyMessage = async () => {
        if (!credentialsMessage) return;
        try {
            await navigator.clipboard.writeText(credentialsMessage);
            setCopiedMessage(true);
            setTimeout(() => setCopiedMessage(false), 2000);
            toast.success(t('gym.resetPasswordModal.copiedMessage'));
        } catch (err) {
            console.error('Errore nella copia:', err);
        }
    };

    const handleWhatsApp = () => {
        const phoneNorm = normalizePhoneForWhatsApp(member?.phone);
        if (phoneNorm && credentialsMessage) {
            const url = `https://wa.me/${phoneNorm}?text=${encodeURIComponent(credentialsMessage)}`;
            window.open(url, '_blank', 'noopener,noreferrer');
        } else {
            toast.error(t('gym.resetPasswordModal.noPhoneHint'));
        }
    };

    const handleTelegram = () => {
        const phoneNorm = normalizePhoneForWhatsApp(member?.phone);
        if (phoneNorm) {
            window.open(`https://t.me/+${phoneNorm}`, '_blank', 'noopener,noreferrer');
        } else {
            toast.error(t('gym.resetPasswordModal.noPhoneHint'));
        }
    };

    const handleClose = () => {
        setResetResult(null);
        setCopied(false);
        onClose();
    };

    // Genera JSON per QR Code
    const getQRCodeValue = () => {
        if (!resetResult?.temp_password || !resetResult?.user?.username) return '';
        return JSON.stringify({
            username: resetResult.user.username,
            password: resetResult.temp_password,
            timestamp: new Date().toISOString()
        });
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div
                className={`relative mx-4 flex w-full flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl dark:border-white/10 dark:bg-gray-900 dark:shadow-black/35 ${
                    resetResult ? 'max-w-3xl' : 'max-w-md'
                }`}
            >
                {/* Header */}
                <div className="flex shrink-0 items-center justify-between border-b border-gray-200 bg-[#dad8e5] px-5 py-4 pr-12 dark:border-white/10 dark:bg-gray-800">
                    <div className="flex min-w-0 items-center gap-3">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-orange-100 dark:bg-orange-900/40">
                            <Key className="h-5 w-5 text-orange-600 dark:text-orange-300" />
                        </div>
                        <div className="min-w-0">
                            <h3 className="text-lg font-semibold text-gray-900 dark:font-open-sans dark:text-white">
                                Reset Password
                            </h3>
                            <p className="truncate text-sm text-gray-600 dark:text-white/70">
                                Per {member?.name}
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={handleClose}
                        className="absolute right-3 top-3 rounded-lg p-2 transition-colors hover:bg-black/5 dark:hover:bg-white/10"
                        aria-label="Chiudi"
                    >
                        <X size={20} className="text-gray-500 dark:text-white/80" />
                    </button>
                </div>

                {/* Content */}
                <div className="bg-white px-6 py-6 dark:bg-gray-900">
                    {!resetResult ? (
                        // Conferma Reset
                        <div className="text-center">
                            <div className="w-16 h-16 bg-orange-100 dark:bg-orange-900 rounded-full flex items-center justify-center mx-auto mb-4">
                                <User className="w-8 h-8 text-orange-600 dark:text-orange-400" />
                            </div>
                            <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                                Conferma Reset Password
                            </h4>
                            <p className="text-gray-600 dark:text-gray-300 mb-6">
                                Sei sicuro di voler resettare la password per <strong>{member?.name}</strong>?
                                <br />
                                <span className="text-sm text-orange-600 dark:text-orange-400">
                                    Verrà generata una nuova password temporanea.
                                </span>
                            </p>
                            <div className="flex flex-wrap justify-center gap-3">
                                <button
                                    type="button"
                                    onClick={handleClose}
                                    className="rounded-xl bg-gray-100 px-5 py-2.5 font-medium text-gray-800 transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:font-open-sans dark:text-white dark:hover:brightness-110"
                                >
                                    Annulla
                                </button>
                                <button
                                    type="button"
                                    onClick={handleReset}
                                    disabled={loading}
                                    className="flex items-center gap-2 rounded-xl bg-orange-600 px-6 py-2.5 font-medium text-white transition-colors hover:bg-orange-700 disabled:opacity-50 dark:bg-indigo-600 dark:text-white dark:hover:brightness-110"
                                >
                                    {loading ? (
                                        <>
                                            <LoadingSpinner size="small" />
                                            Resetting...
                                        </>
                                    ) : (
                                        <>
                                            <Key size={16} />
                                            Reset Password
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    ) : (
                        // Risultato Reset — layout orizzontale da md: QR a sinistra, credenziali a destra
                        <div className="flex flex-col gap-5">
                            <div className="flex flex-col items-center gap-3 sm:flex-row sm:items-center sm:gap-4 sm:text-left">
                                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/40">
                                    <Check className="h-8 w-8 text-green-600 dark:text-green-400" />
                                </div>
                                <div className="min-w-0 text-center sm:text-left">
                                    <h4 className="text-lg font-medium text-gray-900 dark:text-white">
                                        Password Resettata con Successo!
                                    </h4>
                                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                                        La nuova password temporanea è stata generata per{' '}
                                        <strong>{member?.name}</strong>.
                                    </p>
                                </div>
                            </div>

                            <div className="flex flex-col gap-6 md:flex-row md:items-start md:gap-8">
                                {/* QR Code */}
                                <div className="flex flex-col items-center md:w-[min(100%,260px)] md:shrink-0">
                                    <div className="relative rounded-lg border-2 border-orange-200 bg-white p-3 dark:bg-white">
                                        <QRCode
                                            value={getQRCodeValue()}
                                            size={200}
                                            level="H"
                                            includeMargin
                                        />
                                    </div>
                                    <p className="mt-2 max-w-[220px] text-center text-xs text-gray-600 dark:text-gray-400 sm:text-sm">
                                        📱 Inquadra con l&apos;app per accedere rapidamente
                                    </p>
                                </div>

                                <div className="flex min-w-0 flex-1 flex-col gap-4">
                                    {/* Credenziali */}
                                    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-white/10 dark:bg-gray-900">
                                        <div className="space-y-4">
                                            <div>
                                                <p className="mb-1 text-sm text-gray-500 dark:text-gray-400">
                                                    Username:
                                                </p>
                                                <p className="break-all font-mono text-base text-gray-900 dark:text-white sm:text-lg">
                                                    {resetResult.user?.username || 'N/A'}
                                                </p>
                                            </div>
                                            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                                                <div className="min-w-0 flex-1">
                                                    <p className="mb-1 text-sm text-gray-500 dark:text-gray-400">
                                                        Password Temporanea:
                                                    </p>
                                                    <p className="break-all font-mono text-base text-gray-900 dark:text-white sm:text-lg">
                                                        {resetResult.temp_password}
                                                    </p>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={handleCopy}
                                                    className="flex shrink-0 items-center justify-center gap-2 self-start rounded-lg bg-blue-600 px-4 py-2.5 text-sm text-white transition-colors hover:bg-blue-700 dark:bg-indigo-600 dark:text-white dark:hover:brightness-110 sm:self-auto"
                                                >
                                                    {copied ? (
                                                        <>
                                                            <Check size={16} />
                                                            Copiato!
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Copy size={16} />
                                                            Copia
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Comunica credenziali (WEBAPP-015) */}
                                    <div>
                                        <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                                            {t('gym.resetPasswordModal.communicateTitle')}
                                        </p>
                                        <div className="flex flex-wrap gap-2">
                                            <button
                                                type="button"
                                                onClick={handleCopyMessage}
                                                className="flex items-center gap-2 rounded-lg bg-gray-100 px-4 py-2 text-sm text-gray-800 transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:text-white dark:hover:brightness-110"
                                            >
                                                {copiedMessage ? (
                                                    <>
                                                        <Check size={16} />
                                                        {t('gym.resetPasswordModal.copiedMessage')}
                                                    </>
                                                ) : (
                                                    <>
                                                        <Copy size={16} />
                                                        {t('gym.resetPasswordModal.copyMessage')}
                                                    </>
                                                )}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleWhatsApp}
                                                className="flex items-center gap-2 rounded-md bg-[#25D366] px-4 py-2 text-sm text-white transition-colors hover:bg-[#20BD5A]"
                                                title={member?.phone ? '' : t('gym.resetPasswordModal.noPhoneHint')}
                                            >
                                                <MessageCircle size={16} />
                                                {t('gym.resetPasswordModal.sendWhatsApp')}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleTelegram}
                                                className="flex items-center gap-2 rounded-md bg-[#0088cc] px-4 py-2 text-sm text-white transition-colors hover:bg-[#006699]"
                                                title={member?.phone ? '' : t('gym.resetPasswordModal.noPhoneHint')}
                                            >
                                                <Send size={16} />
                                                {t('gym.resetPasswordModal.sendTelegram')}
                                            </button>
                                        </div>
                                        {!member?.phone && (
                                            <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                                                {t('gym.resetPasswordModal.noPhoneHint')}
                                            </p>
                                        )}
                                    </div>

                                    <div className="flex justify-end border-t border-gray-200 pt-2 dark:border-white/10">
                                        <button
                                            type="button"
                                            onClick={handleClose}
                                            className="rounded-xl bg-gray-600 px-6 py-2.5 font-medium text-white transition-colors hover:bg-gray-700 dark:bg-indigo-600 dark:text-white dark:hover:brightness-110"
                                        >
                                            Chiudi
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
